import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { mt5API } from '../../services/api';
import LoaderOverlay from '../../components/common/LoaderOverlay';

interface TransactionItem {
  id: string | number;
  date: string;
  type: string;
  amount: number;
  status: string;
  comment?: string;
  mt5Login?: number | string;
  mt5Ticket?: number | string;
}

const Wallet = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [totalBalance, setTotalBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [showAllTransactions, setShowAllTransactions] =
    useState<boolean>(false);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const res = await mt5API.getWalletTransactions();

      if (res) {
        let rawList: any[] = [];
        let balanceVal = 0;
        if (res.success && res.data) {
          rawList = res.data.transactions || [];
          balanceVal = Number(res.data.totalBalance || 0);
        } else if (Array.isArray(res)) {
          rawList = res;
        } else if (res.transactions && Array.isArray(res.transactions)) {
          rawList = res.transactions;
          balanceVal = Number(res.totalBalance || 0);
        } else if (res.data) {
          if (Array.isArray(res.data)) {
            rawList = res.data;
          } else if (res.data.transactions) {
            rawList = res.data.transactions;
            balanceVal = Number(res.data.totalBalance || 0);
          }
        }
        const mapped: TransactionItem[] = rawList.map((tx: any) => ({
          id: tx.id || Math.random(),
          mt5Ticket: tx.mt5Ticket || tx.ticket || undefined,
          date: tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : '',
          type: String(tx.type || 'Transaction').toUpperCase(),
          amount:
            String(tx.type).toLowerCase() === 'withdraw' ||
            String(tx.type).toLowerCase() === 'withdrawal'
              ? -Math.abs(Number(tx.amount || 0))
              : Number(tx.amount || 0),
          status:
            (tx.status || 'completed').charAt(0).toUpperCase() +
            (tx.status || 'completed').slice(1),
          comment: tx.comment,
          mt5Login: tx.mt5Login,
        }));

        setTransactions(mapped);

        if (!balanceVal && mapped.length > 0) {
          balanceVal = mapped.reduce((acc, curr) => {
            if (
              curr.status.toLowerCase() === 'success' ||
              curr.status.toLowerCase() === 'completed'
            ) {
              return acc + curr.amount;
            }
            return acc;
          }, 0);
        }

        setTotalBalance(balanceVal);
      }
    } catch (err) {
      console.error('Failed to fetch wallet transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const displayedTransactions = showAllTransactions
    ? transactions
    : transactions.slice(0, 4);

  return (
    <LoaderOverlay isLoading={loading} loadingText="Loading Wallet Data...">
      <div className="space-y-6">
        <div className="pt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4">
              <div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Wallet
                </CardTitle>
              </div>
              <div className="sm:text-right">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Total Balance
                </p>
                <div className="text-2xl sm:text-3xl font-bold text-gray-900 mt-0.5">
                  $
                  {totalBalance.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
                <p className="text-xs text-gray-500">Available for trading</p>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Recent transactions
                  </h3>
                  {transactions.length > 4 && (
                    <span className="text-xs text-gray-500">
                      Showing {displayedTransactions.length} of{' '}
                      {transactions.length}
                    </span>
                  )}
                </div>
                <div
                  className={`mt-4 ${
                    showAllTransactions ? 'max-h-80 overflow-y-auto pr-2' : ''
                  }`}
                >
                  {transactions.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">
                      No recent wallet transactions found.
                    </p>
                  ) : (
                    <ul className="-mt-2 divide-y divide-gray-100">
                      {displayedTransactions.map(tx => (
                        <li key={tx.id} className="py-3 sm:py-3">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {tx.type}{' '}
                                {tx.mt5Login ? `(Account: ${tx.mt5Login})` : ''}
                              </p>
                              <p className="text-xs text-gray-500">
                                {[
                                  tx.mt5Ticket
                                    ? `Ticket #${tx.mt5Ticket}`
                                    : null,
                                  tx.date || null,
                                  tx.comment || null,
                                ]
                                  .filter(Boolean)
                                  .join(' • ')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p
                                className={`text-sm font-semibold ${
                                  tx.amount < 0
                                    ? 'text-red-600'
                                    : 'text-green-600'
                                }`}
                              >
                                {tx.amount < 0 ? '-' : '+'}$
                                {Math.abs(tx.amount).toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {tx.status}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {transactions.length > 4 && (
                  <div className="mt-3 text-center border-t pt-3">
                    <button
                      type="button"
                      onClick={() =>
                        setShowAllTransactions(!showAllTransactions)
                      }
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors focus:outline-none"
                    >
                      {showAllTransactions ? 'Show less' : 'More...'}
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Use these options to manage your wallet quickly.
              </p>
              <div className="space-y-3">
                <Button
                  className="w-full"
                  onClick={() => navigate('/payments/deposit')}
                >
                  Add Funds
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/payments/withdrawal')}
                >
                  Withdraw
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/payments/transfer')}
                >
                  Transfer to Trading Account
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => fetchWalletData()}
                >
                  Refresh History
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </LoaderOverlay>
  );
};

export default Wallet;
